import React from 'react';
import { Alert, Box } from '@mui/material';

const INLINE_FIELD_LIMIT = 3;

const translate = (t, key, defaultValue, options = {}) => {
  const raw = typeof t === 'function'
    ? t(key, { defaultValue, interpolation: { escapeValue: false }, ...options })
    : defaultValue;
  return String(raw).replace(/\{\{(\w+)\}\}/g, (_, token) => (
    options[token] !== undefined ? String(options[token]) : ''
  ));
};

const formatFieldLabel = (label) => {
  const raw = typeof label === 'string' ? label.trim() : '';
  if (!raw) return '';
  if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/i.test(raw)) {
    const spaced = raw.replace(/_/g, ' ');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }
  return raw;
};

const WidgetMissingInformationNotice = ({ information, t }) => {
  if (!information || information.status !== 'incomplete' || !(information.affected_rows > 0)) {
    return null;
  }
  const fields = (Array.isArray(information.fields) ? information.fields.slice(0, 128) : [])
    .filter((item) => typeof item?.label === 'string' && Number.isInteger(item?.count) && item.count > 0)
    .map((item) => ({ label: formatFieldLabel(item.label), count: item.count }))
    .filter((item) => item.label);
  const affected = information.affected_rows;
  const total = Number.isInteger(information.row_count) && information.row_count >= affected
    ? information.row_count
    : null;
  const inlineFields = fields
    .slice(0, INLINE_FIELD_LIMIT)
    .map((item) => `${item.label}: ${item.count}`)
    .join(', ');

  let message;
  if (fields.length > 0 && total !== null) {
    message = translate(
      t,
      'widget.incomplete_information_with_fields_and_total',
      'Partial result: missing from sources on {{affected}} of {{total}} records ({{fields}}).',
      { affected, total, fields: inlineFields },
    );
  } else if (fields.length > 0) {
    message = translate(
      t,
      'widget.incomplete_information_with_fields',
      'Partial result: missing from sources on {{affected}} record(s) ({{fields}}).',
      { affected, fields: inlineFields },
    );
  } else {
    message = translate(
      t,
      'widget.incomplete_information_notice',
      'Partial result: some required values are missing from the sources ({{affected}} record(s) affected).',
      { affected },
    );
  }

  return (
    <Alert severity="info" sx={{ mb: 1, flexShrink: 0 }}>
      {message}
      {fields.length > INLINE_FIELD_LIMIT && (
        <Box component="details" sx={{ mt: 0.5 }}>
          <Box component="summary" sx={{ cursor: 'pointer' }}>
            {translate(t, 'widget.missing_information_details', 'Information to check')}
          </Box>
          <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
            {fields.map((item, index) => <li key={index}>{item.label}: {item.count}</li>)}
          </Box>
        </Box>
      )}
    </Alert>
  );
};

export default WidgetMissingInformationNotice;
